import { useCallback, useState } from 'react';
import { Image, ScrollView, Text, View } from 'react-native';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useAuth } from '../context/AuthContext';
import { getSubmission, reviewSubmission } from '../api/submissions';
import { Submission } from '../types';
import {
  Button,
  Card,
  ConfirmDialog,
  ErrorText,
  FormInput,
  Label,
  LoadingState,
  ScreenContainer,
} from '../components/ui';
import { colors, fonts } from '../theme';
import { ApiError } from '../api/client';
import { StatusBadge, SUBMISSION_STATUS_LABELS } from '../components/StatusBadge';
import { resolvePhotoUrl } from '../config';
import { openExternalUrl } from '../utils/openUrl';

/** Without an onError fallback, a photo that fails to load (dev LAN IP
 * changed, file removed from disk, unsupported format) just renders an
 * empty box with no indication whether that's "no photo" or "failed to
 * load" — directly undermining the approve/reject decision this whole
 * screen exists for. */
function ReviewPhotoThumb({ uri }: { uri: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <View
        style={{
          width: 100,
          height: 100,
          borderRadius: 8,
          backgroundColor: colors.primarySoft,
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
        }}
      >
        <Text style={{ fontSize: 11, color: colors.textMuted, textAlign: 'center' }}>Không tải được ảnh</Text>
      </View>
    );
  }
  return (
    <Image
      source={{ uri }}
      style={{ width: 100, height: 100, borderRadius: 8 }}
      onError={() => setFailed(true)}
    />
  );
}

export default function OwnerSubmissionReviewScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { token } = useAuth();
  const router = useRouter();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [confirmApproveVisible, setConfirmApproveVisible] = useState(false);

  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token || !id) return;
    try {
      setLoadError(null);
      setSubmission(await getSubmission(token, id));
    } catch (e) {
      // Previously unhandled — left the screen stuck on <LoadingState />
      // forever with no way to recover on a bad id / network failure.
      setLoadError(e instanceof ApiError ? e.message : 'Không tải được bài nộp, vui lòng thử lại.');
    }
  }, [token, id]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  const onApprove = async () => {
    if (!token || !submission) return;
    setBusy(true);
    setError(null);
    try {
      await reviewSubmission(token, submission.id, true);
      router.back();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Duyệt bài thất bại');
    } finally {
      setBusy(false);
      setConfirmApproveVisible(false);
    }
  };

  const onReject = async () => {
    if (!token || !submission) return;
    if (!rejectReason.trim()) {
      setError('Vui lòng nhập lý do từ chối');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await reviewSubmission(token, submission.id, false, rejectReason.trim());
      router.back();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Từ chối bài thất bại');
    } finally {
      setBusy(false);
    }
  };

  if (!submission) {
    if (loadError) {
      return (
        <ScreenContainer>
          <ErrorText>{loadError}</ErrorText>
          <Button title="Thử lại" onPress={load} />
        </ScreenContainer>
      );
    }
    return <LoadingState />;
  }
  const task = submission.task!;

  const photoHint = (() => {
    switch (task.taskType) {
      case 'GOOGLE_REVIEW_WITH_PURCHASE':
      case 'GOOGLE_REVIEW_NO_PURCHASE':
        return `Mở bài đánh giá thật trên Google Maps và đối chiếu với những ảnh minh chứng dưới đây — ảnh phải trùng khớp và tên tài khoản đánh giá phải khớp với "${submission.reviewer?.name}" trước khi duyệt.`;
      case 'SOCIAL_CHECKIN':
        return 'Kiểm tra ảnh chụp màn hình bài đăng Facebook/Instagram trong số ảnh dưới đây: đối chiếu bài đã đăng công khai và đúng nội dung yêu cầu trước khi duyệt.';
      case 'MYSTERY_SHOPPER':
        return 'Đối chiếu ảnh minh chứng và nhận xét của người nhận việc với các tiêu chí bạn đã đề ra trong hướng dẫn trước khi duyệt. Đây là nhận xét chủ quan — Loot không xác minh nội dung nhận xét, chỉ xác nhận người nhận việc có thực sự ghé shop qua ảnh và vị trí check-in.';
      default:
        return 'Kiểm tra ảnh minh chứng công việc đã hoàn thành đúng như hướng dẫn trước khi duyệt.';
    }
  })();

  return (
    <ScreenContainer>
      <ScrollView>
        <Card>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Text style={{ fontSize: 16, fontFamily: fonts.bold, color: colors.text, flex: 1 }}>
              {task.title}
            </Text>
            <StatusBadge status={submission.status} />
          </View>
          <Text style={{ color: colors.textMuted, marginTop: 4 }}>
            Người nhận việc: {submission.reviewer?.name} ({submission.reviewer?.email})
          </Text>
          {submission.checkinDistanceMeters != null ? (
            <Text style={{ color: colors.textMuted, marginTop: 4, fontSize: 12 }}>
              Check-in cách shop {Math.round(submission.checkinDistanceMeters)}m
            </Text>
          ) : null}
          {submission.checkinAt ? (
            <Text style={{ color: colors.textMuted, marginTop: 4, fontSize: 12 }}>
              Check-in lúc: {new Date(submission.checkinAt).toLocaleString('vi-VN')}
            </Text>
          ) : null}
          {submission.submittedAt ? (
            <Text style={{ color: colors.textMuted, marginTop: 4, fontSize: 12 }}>
              Nộp bài lúc: {new Date(submission.submittedAt).toLocaleString('vi-VN')}
            </Text>
          ) : null}
        </Card>

        {submission.referredCustomerName || submission.referredCustomerPhone ? (
          <Card style={{ borderColor: colors.warning, borderWidth: 1 }}>
            <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 6 }}>
              Khách được giới thiệu
            </Text>
            <Text style={{ color: colors.text }}>Tên: {submission.referredCustomerName || '—'}</Text>
            <Text style={{ color: colors.text, marginTop: 2 }}>
              SĐT: {submission.referredCustomerPhone || '—'}
            </Text>
            <Text style={{ color: colors.textMuted, fontSize: 12, marginTop: 8 }}>
              Đối chiếu với khách thực tế đã ghé shop trước khi duyệt — không có cách xác minh tự động
              cho loại nhiệm vụ này.
            </Text>
          </Card>
        ) : null}

        <Card>
          <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 4 }}>
            Ảnh minh chứng ({submission.photos.length})
          </Text>
          <Text style={{ color: colors.textMuted, fontSize: 12, marginBottom: 8 }}>{photoHint}</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {submission.photos.map((p) => (
              <ReviewPhotoThumb key={p.id} uri={resolvePhotoUrl(p.url)} />
            ))}
          </View>
        </Card>

        {submission.googleReviewUrl || submission.reviewerNote ? (
          <Card>
            {submission.googleReviewUrl ? (
              <>
                <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 8 }}>
                  Đường dẫn bài đánh giá Google Maps
                </Text>
                <Text
                  style={{ color: colors.primary }}
                  onPress={() => openExternalUrl(submission.googleReviewUrl!)}
                >
                  {submission.googleReviewUrl}
                </Text>
              </>
            ) : null}
            {submission.reviewerNote ? (
              <>
                {/* Standalone header (not just "Ghi chú:") when there's no
                 * Maps link above it — for task types like MYSTERY_SHOPPER
                 * this note IS the entire deliverable, not a side remark. */}
                <Text
                  style={{
                    fontFamily: fonts.bold,
                    color: colors.text,
                    marginBottom: 6,
                    marginTop: submission.googleReviewUrl ? 8 : 0,
                  }}
                >
                  {submission.googleReviewUrl ? 'Ghi chú' : 'Nhận xét của người nhận việc'}
                </Text>
                <Text style={{ color: colors.text }}>{submission.reviewerNote}</Text>
              </>
            ) : null}
          </Card>
        ) : null}

        <ErrorText>{error}</ErrorText>

        {submission.status === 'SUBMITTED' ? (
          showRejectForm ? (
            <Card>
              <Label>Lý do từ chối</Label>
              <FormInput
                value={rejectReason}
                onChangeText={setRejectReason}
                placeholder="Ví dụ: ảnh không rõ nét, không đúng shop"
              />
              <Button title="Xác nhận từ chối" variant="danger" onPress={onReject} loading={busy} />
              <Button title="Huỷ" variant="outline" onPress={() => setShowRejectForm(false)} />
            </Card>
          ) : (
            <View style={{ gap: 10 }}>
              <Button
                title="Duyệt và trả thưởng"
                onPress={() => setConfirmApproveVisible(true)}
                loading={busy}
              />
              <Button
                title="Từ chối"
                variant="danger"
                onPress={() => setShowRejectForm(true)}
                disabled={busy}
              />
            </View>
          )
        ) : (
          <Card>
            <Text style={{ color: colors.textMuted }}>
              Bài nộp này đã được xử lý ({SUBMISSION_STATUS_LABELS[submission.status] ?? submission.status}), không thể duyệt lại.
            </Text>
          </Card>
        )}

        {submission.disputeStatus === 'OPEN' ? (
          <Card style={{ borderColor: colors.warning, borderWidth: 1 }}>
            <Text style={{ color: colors.warning, fontFamily: fonts.bold }}>
              Người nhận việc đã khiếu nại quyết định từ chối này
            </Text>
            <Text style={{ color: colors.textMuted, marginTop: 4, fontSize: 13 }}>
              Admin sẽ xem xét và có thể ghi đè quyết định này nếu thấy bằng chứng hợp lý.
            </Text>
          </Card>
        ) : submission.disputeStatus === 'RESOLVED_REVIEWER' ? (
          <Card style={{ borderColor: colors.danger, borderWidth: 1 }}>
            <Text style={{ color: colors.danger, fontFamily: fonts.bold }}>
              Admin đã chấp nhận khiếu nại — bài nộp này đã được duyệt lại và trả thưởng
            </Text>
          </Card>
        ) : null}
      </ScrollView>

      <ConfirmDialog
        visible={confirmApproveVisible}
        title="Xác nhận duyệt bài"
        message="Duyệt bài nộp này sẽ trả thưởng ngay cho người nhận việc và không thể hoàn tác. Bạn có chắc chắn muốn tiếp tục?"
        confirmLabel="Duyệt và trả thưởng"
        loading={busy}
        onConfirm={onApprove}
        onCancel={() => setConfirmApproveVisible(false)}
      />
    </ScreenContainer>
  );
}
